import { type FC } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Pagination } from '@/components/Pagination';
import useIsMobile from '@/hooks/useIsMobile';
import { type IOrderListItem } from '@/types/IOrder';
import { type IErrorData } from '@/utils/getErrorData';
import { getCallOrderSummary } from '@/utils/getCallOrderSummary';
import { CallOrdersTableSkeleton } from '../CallOrdersTableSkeleton/CallOrdersTableSkeleton';
import styles from './callOrdersTable.module.scss';

interface ICallOrdersTableProps {
  orders: IOrderListItem[];
  total: number;
  page: number;
  limit: number;
  isLoading: boolean;
  error: IErrorData | null;
  /** Текст пустого состояния — у каждой выборки свой. */
  emptyText: string;
  /** Показывать колонку «Клиент» — в выборке по номеру получателя. */
  showClient: boolean;
  /**
   * Ключ учётки звонящего — `uuid`, у прежнего бэкенда `id` (PAR-5189) —
   * либо `null`. Для метки «Заказ клиента».
   */
  accountId: string | number | null;
  onPageChange: (page: number) => void;
  onRetry: () => void;
}

const RETRY_LABEL = 'Повторить';
const CLIENT_COLUMN_LABEL = 'Клиент';
const OWN_ORDER_LABEL = 'Заказ клиента';
const OWN_ORDER_HINT = 'Этот заказ есть и во вкладке «Заказы клиента»';

/**
 * Таблица заказов в модалке звонка: новые сверху, строка открывает заказ в
 * новой вкладке. Данные приходят готовыми — компонент только рендерит
 * состояния «загрузка / ошибка / пусто / список» и переключает страницы.
 * Заголовок не рисует: его роль выполняет полоса вкладок над таблицей.
 */
export const CallOrdersTable: FC<ICallOrdersTableProps> = ({
  orders,
  total,
  page,
  limit,
  isLoading,
  error,
  emptyText,
  showClient,
  accountId,
  onPageChange,
  onRetry
}) => {
  const { isMobile } = useIsMobile();

  const openInNewTab = (path: string): void => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const hasPagination = error === null && total > limit;

  return (
    <section className={styles['call-orders-table']}>
      {isLoading && <CallOrdersTableSkeleton withClient={showClient} />}

      {!isLoading && error !== null && (
        <div className={styles['call-orders-table__error']}>
          <p className={styles['call-orders-table__message']}>
            {error.message}
          </p>
          <button
            type="button"
            className={styles['call-orders-table__retry']}
            onClick={onRetry}
          >
            {RETRY_LABEL}
          </button>
        </div>
      )}

      {!isLoading && error === null && orders.length === 0 && (
        <p className={styles['call-orders-table__message']}>{emptyText}</p>
      )}

      {!isLoading && error === null && orders.length > 0 && (
        <div className={styles['call-orders-table__frame']}>
          <div className={styles['call-orders-table__scroller']}>
            <table className={styles['call-orders-table__table']}>
              <thead>
                <tr>
                  <th scope="col">Номер</th>
                  {showClient && <th scope="col">{CLIENT_COLUMN_LABEL}</th>}
                  <th scope="col">Дата</th>
                  <th scope="col">Статус</th>
                  <th scope="col">Оплата</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const summary = getCallOrderSummary(order, accountId);

                  return (
                    <tr
                      key={summary.uuid}
                      className={cn(styles['call-orders-table__row'], {
                        [styles['call-orders-table__row_own']]:
                          showClient && summary.isClientOrder
                      })}
                      onClick={() => {
                        openInNewTab(summary.orderPath);
                      }}
                    >
                      <td>
                        <Link
                          className={styles['call-orders-table__link']}
                          to={summary.orderPath}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={summary.fullNumber}
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          {summary.number}
                        </Link>
                      </td>
                      {showClient && (
                        <td>
                          <span className={styles['call-orders-table__client']}>
                            <span>{summary.clientName}</span>
                            {summary.isClientOrder && (
                              <span
                                className={styles['call-orders-table__own']}
                                title={OWN_ORDER_HINT}
                              >
                                {OWN_ORDER_LABEL}
                              </span>
                            )}
                          </span>
                        </td>
                      )}
                      <td>{summary.date}</td>
                      <td>{summary.status}</td>
                      <td>{summary.paymentStatus}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {hasPagination && (
        <div className={styles['call-orders-table__pagination']}>
          <Pagination
            currentPage={page}
            total={total}
            limit={limit}
            onPaginationChange={onPageChange}
            isMobile={isMobile}
          />
        </div>
      )}
    </section>
  );
};
